import { HorarioFuncionamento, HorarioExcecao } from '@/app/lib/definitions';

export interface StoreStatusResult {
    isOpen: boolean;
    badge: string; // 'Loja Aberta' | 'Loja Fechada' | 'Feriado'
    color: 'green' | 'amber' | 'red';
    detail: string; // 'Até às 23:00' | 'Abre hoje às 11:00' | 'Feriado: Natal' | 'Fechada hoje'
    isHoliday?: boolean;
    holidayName?: string;
}

export function getStoreStatus(
    horarios: HorarioFuncionamento[] = [],
    excecoes: HorarioExcecao[] = []
): StoreStatusResult {
    const now = new Date();
    const ano = now.getFullYear();
    const mes = String(now.getMonth() + 1).padStart(2, '0');
    const dia = String(now.getDate()).padStart(2, '0');
    const todayStr = `${ano}-${mes}-${dia}`;
    const diaSemana = now.getDay();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // 1. Verifica exceção ou feriado para a data de hoje
    const excecaoHoje = excecoes.find(e => {
        const dStr = typeof e.data === 'string' ? e.data.slice(0, 10) : '';
        return dStr === todayStr;
    });

    let config: {
        aberto: boolean;
        delivery_ativo: boolean;
        delivery_inicio: string;
        delivery_fim: string;
        balcao_ativo: boolean;
        balcao_inicio: string;
        balcao_fim: string;
        descricao?: string;
    } | undefined = undefined;

    let isHoliday = false;
    let holidayName = '';

    const isTrue = (val: any) => val === true || String(val) === 'true';

    if (excecaoHoje) {
        isHoliday = true;
        holidayName = excecaoHoje.descricao;
        config = {
            aberto: isTrue(excecaoHoje.aberto),
            delivery_ativo: isTrue(excecaoHoje.delivery_ativo),
            delivery_inicio: excecaoHoje.delivery_inicio || '11:00',
            delivery_fim: excecaoHoje.delivery_fim || '23:00',
            balcao_ativo: isTrue(excecaoHoje.balcao_ativo),
            balcao_inicio: excecaoHoje.balcao_inicio || '08:00',
            balcao_fim: excecaoHoje.balcao_fim || '22:00',
            descricao: excecaoHoje.descricao
        };
    } else {
        const horarioHoje = horarios.find(h => Number(h.dia_semana) === diaSemana);
        if (horarioHoje) {
            config = {
                aberto: isTrue(horarioHoje.aberto),
                delivery_ativo: isTrue(horarioHoje.delivery_ativo),
                delivery_inicio: horarioHoje.delivery_inicio || '11:00',
                delivery_fim: horarioHoje.delivery_fim || '23:00',
                balcao_ativo: isTrue(horarioHoje.balcao_ativo),
                balcao_inicio: horarioHoje.balcao_inicio || '08:00',
                balcao_fim: horarioHoje.balcao_fim || '22:00'
            };
        }
    }

    if (!config || !config.aberto) {
        return {
            isOpen: false,
            badge: isHoliday ? 'Feriado' : 'Loja Fechada',
            color: 'red',
            detail: isHoliday ? `Feriado: ${holidayName || 'Hoje'}` : 'Fechada hoje',
            isHoliday,
            holidayName
        };
    }

    // Helper seguro para converter HH:MM em minutos
    const parseTimeMinutes = (timeStr: string, defaultVal: number) => {
        if (!timeStr) return defaultVal;
        const parts = timeStr.split(':').map(Number);
        if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return defaultVal;
        return parts[0] * 60 + parts[1];
    };

    // Calcula os intervalos de atendimento ativos hoje (Balcão e Delivery)
    const ranges: { start: number; end: number; tipo: string }[] = [];
    if (config.balcao_ativo && config.balcao_inicio && config.balcao_fim) {
        ranges.push({
            start: parseTimeMinutes(config.balcao_inicio, 480),
            end: parseTimeMinutes(config.balcao_fim, 1320),
            tipo: 'Balcão'
        });
    }
    if (config.delivery_ativo && config.delivery_inicio && config.delivery_fim) {
        ranges.push({
            start: parseTimeMinutes(config.delivery_inicio, 660),
            end: parseTimeMinutes(config.delivery_fim, 1380),
            tipo: 'Delivery'
        });
    }

    if (ranges.length === 0) {
        return {
            isOpen: false,
            badge: 'Loja Fechada',
            color: 'red',
            detail: 'Sem canais ativos hoje',
            isHoliday,
            holidayName
        };
    }

    const minStart = Math.min(...ranges.map(r => r.start));
    const openRanges = ranges.filter(r => {
        if (r.end > r.start) {
            return currentMinutes >= r.start && currentMinutes < r.end;
        } else {
            // Suporta horários que passam da meia-noite (ex: 18:00 às 02:00)
            return currentMinutes >= r.start || currentMinutes < r.end;
        }
    });

    if (openRanges.length > 0) {
        const maxEndMinutes = Math.max(...openRanges.map(r => r.end));
        const closeH = String(Math.floor(maxEndMinutes / 60)).padStart(2, '0');
        const closeM = String(maxEndMinutes % 60).padStart(2, '0');
        return {
            isOpen: true,
            badge: 'Loja Aberta',
            color: 'green',
            detail: `Até às ${closeH}:${closeM}`,
            isHoliday,
            holidayName
        };
    } else if (currentMinutes < minStart) {
        const openH = String(Math.floor(minStart / 60)).padStart(2, '0');
        const openM = String(minStart % 60).padStart(2, '0');
        return {
            isOpen: false,
            badge: 'Loja Fechada',
            color: 'amber',
            detail: `Abre hoje às ${openH}:${openM}`,
            isHoliday,
            holidayName
        };
    } else {
        return {
            isOpen: false,
            badge: 'Loja Fechada',
            color: 'red',
            detail: 'Atendimento encerrado por hoje',
            isHoliday,
            holidayName
        };
    }
}
